import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  FaFile, FaUpload, FaTrash, FaEye, FaFileAlt,
  FaExclamationCircle, FaCheck, FaSync, FaPlus,
  FaShieldAlt, FaTimes, FaLock, FaEdit
} from 'react-icons/fa';
import { documentService } from '../services/document.service';
import { aiControlsService } from '../services/aiControls.service';
import { API_URL } from '../config';

// Styled components
const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 500;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    color: #007bff;
  }
`;

const ActionButton = styled.button`
  margin: 0 2px;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 400;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.primary ? '#007bff' : props.danger ? '#dc3545' : props.success ? '#28a745' : '#f8f9fa'};
  color: ${props => (props.primary || props.danger || props.success) ? 'white' : '#212529'};
  border: 1px solid ${props => props.primary ? '#007bff' : props.danger ? '#dc3545' : props.success ? '#28a745' : '#dee2e6'};
  
  &:hover {
    background-color: ${props => props.primary ? '#0069d9' : props.danger ? '#c82333' : props.success ? '#218838' : '#e2e6ea'};
    border-color: ${props => props.primary ? '#0062cc' : props.danger ? '#bd2130' : props.success ? '#1e7e34' : '#dae0e5'};
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 20px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  background-color: #fafafa;
  font-weight: 500;
  color: #595959;
  font-size: 14px;
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  font-size: 14px;
`;

const ActionCell = styled(Td)`
  text-align: right;
  white-space: nowrap;
  padding: 8px 16px;
`;

const EmptyTableMessage = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #666;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  background-color: ${props =>
    props.status === 'processed' ? '#e6f7e6' :
      props.status === 'processing' ? '#fff7e6' :
        props.status === 'failed' ? '#ffebee' : '#e6f4ff'};
  color: ${props =>
    props.status === 'processed' ? '#28a745' :
      props.status === 'processing' ? '#fa8c16' :
        props.status === 'failed' ? '#dc3545' : '#1890ff'};
  font-weight: 500;
`;

const RuleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  background-color: ${props => props.isRule ? '#e6f7ff' : '#f5f5f5'};
  color: ${props => props.isRule ? '#1890ff' : '#595959'};
  font-weight: 500;
  gap: 4px;
`;

const Alert = styled.div`
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${props =>
    props.type === 'success' ? '#f6ffed' :
      props.type === 'warning' ? '#fffbe6' :
        props.type === 'error' ? '#fff2f0' : '#e6f7ff'};
  border: 1px solid ${props =>
    props.type === 'success' ? '#b7eb8f' :
      props.type === 'warning' ? '#ffe58f' :
        props.type === 'error' ? '#ffccc7' : '#91caff'};
  color: ${props =>
    props.type === 'success' ? '#52c41a' :
      props.type === 'warning' ? '#faad14' :
        props.type === 'error' ? '#f5222d' : '#1890ff'};
`;

const AlertIcon = styled.span`
  display: flex;
  align-items: center;
  font-size: 16px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 4px;
  width: 500px;
  max-width: 90%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e6e6e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: #999;
  
  &:hover {
    color: #666;
  }
`;

const ModalBody = styled.div`
  padding: 16px;
`;

const ModalFooter = styled.div`
  padding: 16px;
  border-top: 1px solid #e6e6e6;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-size: 14px;
  color: #595959;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const FormTextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const FileInput = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  background-color: #fafafa;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    border-color: #1890ff;
  }
  
  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
`;

const FileName = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #595959;
`;

const DocumentTypeTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  background-color: #f0f5ff;
  color: #2f54eb;
  font-weight: 500;
  gap: 4px;
`;

// DocumentUploadModal component
function DocumentUploadModal({ isOpen, onClose, onSuccess }) {
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isRuleDocument, setIsRuleDocument] = useState(true);
  const [uploadMode, setUploadMode] = useState('single'); // 'single' or 'batch'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setError('');
      if (uploadMode === 'single') {
        setFiles([e.target.files[0]]);
      } else {
        // Convert FileList to Array for multiple files
        setFiles(Array.from(e.target.files));
      }
    }
  };

  const toggleUploadMode = () => {
    setUploadMode(prev => prev === 'single' ? 'batch' : 'single');
    setFiles([]); // Clear selected files when switching modes
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (files.length === 0) {
      setError('Please select file(s) to upload');
      setLoading(false);
      return;
    }

    try {
      if (uploadMode === 'single') {
        // Single file upload
        const formData = new FormData();
        formData.append('file', files[0]);
        formData.append('title', title || files[0].name);
        formData.append('description', description);
        formData.append('is_rule_document', isRuleDocument ? '1' : '0');

        const res = await documentService.uploadDocument(formData);

        if (res.success) {
          onSuccess(res.document);
          onClose();
        } else {
          setError(res.message || 'Failed to upload document');
        }
      } else {
        // Batch upload
        const formData = new FormData();

        console.log(`Preparing to upload ${files.length} files in batch mode`);

        // Append each file to the form data
        files.forEach((file, index) => {
          console.log(`Adding file ${index + 1}/${files.length}: ${file.name} (${file.size} bytes)`);
          formData.append('files', file);
        });

        // Add shared description if provided
        if (description) {
          formData.append('description', description);
        }

        // Add document type
        formData.append('is_rule_document', isRuleDocument ? '1' : '0');

        console.log('Sending batch upload request...');
        const res = await documentService.uploadDocumentsBatch(formData);
        console.log('Batch upload response:', res);

        if (res.success) {
          // If any documents were successfully uploaded
          if (res.documents && res.documents.length > 0) {
            // Add all uploaded documents to the list
            res.documents.forEach(doc => onSuccess(doc));
            onClose();
          } else {
            setError('No documents were successfully uploaded');
          }
        } else {
          setError(res.message || 'Failed to upload documents');
        }
      }
    } catch (e) {
      console.error(`Error uploading document(s):`, e);
      setError(e.response?.data?.message || 'Failed to upload document(s)');
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle><FaUpload /> Upload AI Control Rule</ModalTitle>
          <CloseButton onClick={onClose}><FaTimes /></CloseButton>
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <FormLabel htmlFor="file">Rule Document File</FormLabel>
                <div>
                  <ActionButton
                    type="button"
                    onClick={toggleUploadMode}
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                  >
                    {uploadMode === 'single' ? 'Switch to Batch Upload' : 'Switch to Single Upload'}
                  </ActionButton>
                </div>
              </div>
              <FileInput>
                <FileInputLabel>
                  <FaUpload /> {uploadMode === 'single' ? 'Click to upload a file' : 'Click to upload multiple files'}
                  <input
                    type="file"
                    id="file"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.txt,.md"
                    multiple={uploadMode === 'batch'}
                  />
                </FileInputLabel>
                {files.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    {files.length === 1 ? (
                      <FileName>{files[0].name}</FileName>
                    ) : (
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{files.length} files selected:</div>
                        {files.map((file, index) => (
                          <FileName key={index} style={{ marginLeft: '10px' }}>
                            • {file.name}
                          </FileName>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </FileInput>
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="title">Rule Document Title</FormLabel>
              <Input
                id="title"
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Enter rule document title or leave blank to use filename"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="description">Description (Optional)</FormLabel>
              <Input
                id="description"
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Enter a brief description of the rule document"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="documentType">Document Type</FormLabel>
              <select
                id="documentType"
                value={isRuleDocument ? 'rule' : 'regular'}
                onChange={e => setIsRuleDocument(e.target.value === 'rule')}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #d9d9d9',
                  marginBottom: '10px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="regular">Regular Document</option>
                <option value="rule">AI Usage Rules Document</option>
              </select>
              {isRuleDocument && (
                <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                  This document will be used to validate AI queries against your organization's usage policies.
                </div>
              )}
            </FormGroup>
            {error && (
              <Alert type="error">
                <AlertIcon><FaExclamationCircle /></AlertIcon>
                {error}
              </Alert>
            )}
            <ModalFooter>
              <ActionButton type="button" onClick={onClose}>Cancel</ActionButton>
              <ActionButton primary type="submit" disabled={loading}>
                {loading ? <><FaSync className="fa-spin" /> Uploading...</> : <><FaUpload /> Upload</>}
              </ActionButton>
            </ModalFooter>
          </Form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
}

// Main DocumentsView component
function DocumentsView() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [customControls, setCustomControls] = useState([]);
  const [showAddControlModal, setShowAddControlModal] = useState(false);
  const [processingDocId, setProcessingDocId] = useState(null);
  const [processingDocuments, setProcessingDocuments] = useState([]);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [showEditControlModal, setShowEditControlModal] = useState(false);
  const [editingControl, setEditingControl] = useState(null);
  const [editingControlIndex, setEditingControlIndex] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState('user');
  const [hasAdminRules, setHasAdminRules] = useState(false);

  useEffect(() => {
    // Get user role from localStorage
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUserRole(userInfo.role || 'user');

    fetchDocuments();
    fetchCustomControls();

    // Clean up polling interval on component unmount
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const fetchCustomControls = async () => {
    try {
      setLoading(true);
      const response = await aiControlsService.getAllRules();
      
      if (response && response.success && response.data) {
        // Transform the data to include the rules content
        const transformedControls = response.data.map(rule => {
          // For each rule, check if it has a rules field
          let ruleContent = '';
          
          if (rule.rules) {
            ruleContent = rule.rules;
          } else if (rule.terms) {
            // Fallback to terms if rules doesn't exist
            ruleContent = rule.terms;
          }
          
          return {
            id: rule.id,
            category: rule.category || 'AI Control Rules',
            rules: ruleContent,
            terms: ruleContent, // For backward compatibility
            created_at: rule.created_at,
            created_by: rule.created_by,
            user_id: rule.user_id,
            enabled: rule.is_enabled !== undefined ? rule.is_enabled : true // Use is_enabled from database
          };
        });
        
        setCustomControls(transformedControls);
      } else {
        setMessage({ text: 'Failed to load AI controls. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Error fetching AI controls:', error);
      setMessage({ text: 'Failed to load AI controls. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const res = await documentService.getDocuments();

      if (res.success) {
        setDocuments(res.documents);

        // Check for documents with 'processing' status
        const processingDocs = res.documents.filter(doc => doc.status === 'processing');

        // If there are processing documents, start polling for updates
        if (processingDocs.length > 0) {
          setProcessingDocuments(processingDocs.map(doc => doc.id));
          startStatusPolling();
        } else if (pollingInterval && processingDocuments.length === 0) {
          // If no more processing documents, stop polling
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
      } else {
        console.error('Invalid response format:', res);
        setDocuments([]);
        setMessage({
          type: 'error',
          text: 'Failed to load documents: Invalid response format'
        });
      }
    } catch (e) {
      console.error('Error fetching documents:', e);
      setDocuments([]);
      setMessage({
        type: 'error',
        text: e.response?.data?.message || 'API connection failed. Please check server connection.'
      });
    }

    setLoading(false);
  };

  const fetchDefaultRules = async () => {
    // Removed the fetchDefaultRules function
  };

  const handleDocumentUploaded = (document) => {
    setMessage({
      type: 'success',
      text: `Document '${document.title}' uploaded successfully!`
    });
    fetchDocuments();
  };

  const deleteDocument = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    setMessage({ type: '', text: '' });
    try {
      await documentService.deleteDocument(id);
      setMessage({ type: 'success', text: 'Document deleted successfully' });
      fetchDocuments();
    } catch (e) {
      setMessage({
        type: 'error',
        text: e.response?.data?.message || 'Failed to delete document'
      });
    }
  };

  // Start polling for document status updates
  const startStatusPolling = () => {
    // Only start polling if it's not already running
    if (!pollingInterval) {
      console.log('Starting status polling for processing documents');
      const interval = setInterval(() => {
        if (processingDocuments.length > 0) {
          console.log('Polling for document status updates...');
          fetchDocuments();
        } else {
          // If no more processing documents, stop polling
          clearInterval(interval);
          setPollingInterval(null);
        }
      }, 5000); // Poll every 5 seconds

      setPollingInterval(interval);
    }
  };

  const processDocument = async (id) => {
    setProcessingDocId(id);
    setMessage({ type: '', text: '' });

    try {
      await documentService.processDocument(id);
      setMessage({ type: 'success', text: 'Document processing started successfully' });

      // Add this document to the processing list
      setProcessingDocuments(prev => [...prev, id]);

      // Start polling for status updates
      startStatusPolling();

      // Fetch documents to update the UI
      fetchDocuments();
    } catch (e) {
      setMessage({
        type: 'error',
        text: e.response?.data?.message || 'Failed to process document'
      });
    }

    setProcessingDocId(null);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'processed':
        return <StatusBadge status="processed"><FaCheck /> Processed</StatusBadge>;
      case 'processing':
        return <StatusBadge status="processing"><FaSync /> Processing</StatusBadge>;
      case 'failed':
        return <StatusBadge status="failed"><FaExclamationCircle /> Failed</StatusBadge>;
      default:
        return <StatusBadge status="pending"><FaFile /> Pending</StatusBadge>;
    }
  };

  const getRuleBadge = (isRuleDocument) => {
    // Convert to boolean if it's a number or string
    const isRule = isRuleDocument === 1 || isRuleDocument === '1' || isRuleDocument === true;

    if (isRule) {
      return <RuleBadge isRule={true}><FaShieldAlt /> AI Rule</RuleBadge>;
    } else {
      return <RuleBadge isRule={false}><FaTimes /> Not a Rule</RuleBadge>;
    }
  };

  const getDocumentTypeTag = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();

    switch (extension) {
      case 'pdf':
        return <DocumentTypeTag><FaFileAlt /> PDF</DocumentTypeTag>;
      case 'doc':
      case 'docx':
        return <DocumentTypeTag><FaFileAlt /> Word</DocumentTypeTag>;
      case 'txt':
        return <DocumentTypeTag><FaFileAlt /> Text</DocumentTypeTag>;
      case 'md':
        return <DocumentTypeTag><FaFileAlt /> Markdown</DocumentTypeTag>;
      default:
        return <DocumentTypeTag><FaFileAlt /> Document</DocumentTypeTag>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleControlDelete = async (controlId, index) => {
    try {
      setLoading(true);

      // Call the API to delete the rule
      const response = await aiControlsService.deleteRule(controlId);

      if (response.success) {
        // Remove the rule from the local state
        const updatedControls = [...customControls];
        updatedControls.splice(index, 1);
        setCustomControls(updatedControls);

        setMessage({
          type: 'success',
          text: 'AI control rule deleted successfully'
        });
      } else {
        setMessage({
          type: 'error',
          text: 'Failed to delete AI control rule'
        });
      }
    } catch (error) {
      console.error('Error deleting control rule:', error);
      setMessage({
        type: 'error',
        text: 'An error occurred while deleting the AI control rule'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleControlEdit = (control, index) => {
    setEditingControl(control);
    setEditingControlIndex(index);
    setShowEditControlModal(true);
  };

  const handleToggleRuleEnabled = async (ruleId, isEnabled, index) => {
    try {
      // Optimistically update the UI
      const updatedControls = [...customControls];
      updatedControls[index].enabled = isEnabled;
      setCustomControls(updatedControls);
      
      // Call the API to update the database
      await aiControlsService.toggleRuleEnabled(ruleId, isEnabled);
      
      // Show success message
      setMessage(`Rule ${isEnabled ? 'enabled' : 'disabled'} successfully`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error toggling rule enabled state:', error);
      setMessage(`Failed to ${isEnabled ? 'enable' : 'disable'} rule. Please try again.`);
      
      // Revert the UI change on error
      const updatedControls = [...customControls];
      updatedControls[index].enabled = !isEnabled;
      setCustomControls(updatedControls);
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setMessage('');
      }, 3000);
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle><FaShieldAlt /> AI Controls</PageTitle>
      </PageHeader>

      {message.text && (
        <Alert type={message.type}>
          <AlertIcon>
            {message.type === 'success' ? <FaCheck /> :
              message.type === 'error' ? <FaExclamationCircle /> :
                <FaExclamationCircle />}
          </AlertIcon>
          {message.text}
        </Alert>
      )}

      {/* Main content */}
      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <FaSync className="fa-spin" /> Loading...
          </div>
        ) : documents.length > 0 ? (
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th><div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FaShieldAlt /> Rule Document</div></th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Uploaded</th>
                  <th>Status</th>
                  <th>AI Rule</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr key={doc.id}>
                    <td>{doc.filename}</td>
                    <td>{getDocumentTypeTag(doc.filename)}</td>
                    <td>{doc.description || '-'}</td>
                    <td>{formatDate(doc.uploaded_at)}</td>
                    <td>{getStatusBadge(doc.status)}</td>
                    <td>{getRuleBadge(doc.is_rule_document)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <ActionButton onClick={() => window.open(`${API_URL}/documents/${doc.id}/download`, '_blank')}>
                        <FaEye /> View
                      </ActionButton>{' '}
                      {doc.status === 'uploaded' && (
                        <ActionButton success onClick={() => processDocument(doc.id)} disabled={processingDocId === doc.id}>
                          {processingDocId === doc.id ? <><FaSync className="fa-spin" /> Processing...</> : <><FaCheck /> Process</>}
                        </ActionButton>
                      )}{' '}
                      <ActionButton danger onClick={() => deleteDocument(doc.id)}>
                        <FaTrash /> Delete
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyTableMessage>

            {/* AI Controls */}
         
              <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ marginLeft: '10px', fontSize: '18px' }}><FaShieldAlt /> AI Controls</h3>
                  <div>
                    {customControls.length === 0 && (
                      <ActionButton
                        primary
                        onClick={() => setShowAddControlModal(true)}
                        title="Add AI Control"
                      >
                        <FaPlus /> Add AI Control
                      </ActionButton>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    border: '1px solid #e8e8e8',
                    borderRadius: '5px',
                    padding: '15px'
                  }}
                >
                  {customControls.map((control, index) => (
                    <div key={index} style={{
                      background: control.enabled ? '#f0f7ff' : '#f9f9f9',
                      padding: '15px',
                      borderRadius: '5px',
                      marginBottom: '15px',
                      border: '1px solid #d6e4ff',
                      opacity: control.enabled ? 1 : 0.6
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <strong style={{ color: '#1890ff', fontSize: '16px' }}>{control.category}</strong>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {/* <span style={{ fontSize: '12px', color: '#666' }}>
                            Created: {formatDate(control.created_at)}
                            {control.created_by && <> by ID: {control.created_by}</>}
                          </span> */}
                          <div>
                            <ActionButton
                              success={control.enabled}
                              danger={!control.enabled}
                              style={{ padding: '2px 5px', fontSize: '12px', marginRight: '5px' }}
                              onClick={() => handleToggleRuleEnabled(control.id, !control.enabled, index)}
                            >
                              {control.enabled ? (
                                <>
                                  <FaCheck size={12} /> Enabled
                                </>
                              ) : (
                                <>
                                  <FaTimes size={12} /> Disabled
                                </>
                              )}
                            </ActionButton>
                            <ActionButton style={{ padding: '2px 5px', fontSize: '12px', marginRight: '5px' }} onClick={() => handleControlEdit(control, index)}>
                              <FaEdit size={12} /> Edit
                            </ActionButton>
                            <ActionButton danger style={{ padding: '2px 5px', fontSize: '12px' }} onClick={() => handleControlDelete(control.id, index)}>
                              <FaTimes size={12} /> Remove
                            </ActionButton>
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '14px', marginBottom: '8px', lineHeight: '1.5', textAlign: 'left' }}>
                        <strong style={{ textAlign: 'left' }}>Rules:</strong>
                        <pre style={{
                          background: '#f9f9f9',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #eee',
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          marginTop: '5px',
                          maxHeight: '150px',
                          overflowY: 'auto',
                          textAlign: 'left'
                        }}>
                          {control.rules || control.terms}
                        </pre>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
          

            {/* Empty state actions */}
          </EmptyTableMessage>
        )}
      </Card>

      {/* Add AI Control modal */}
      {showAddControlModal && (
        <AddAIControlModal
          isOpen={showAddControlModal}
          onClose={() => setShowAddControlModal(false)}
          onSuccess={(control) => {
            setCustomControls([...customControls, control]);
            setMessage({
              text: 'AI control rule added successfully!',
              type: 'success'
            });
            setTimeout(() => setMessage({ text: '', type: '' }), 5000);
          }}
        />
      )}

      {showEditControlModal && (
        <EditControlModal
          isOpen={showEditControlModal}
          onClose={() => setShowEditControlModal(false)}
          control={editingControl}
          onSuccess={(updatedControl) => {
            // Update the control in the local state
            const updatedControls = [...customControls];
            updatedControls[editingControlIndex] = updatedControl;
            setCustomControls(updatedControls);

            // Update the control in the backend
            aiControlsService.updateRule(updatedControl.id, updatedControl)
              .then(response => {
                if (response.success) {
                  setMessage({
                    type: 'success',
                    text: 'AI control rule updated successfully'
                  });
                } else {
                  setMessage({
                    type: 'error',
                    text: 'Failed to update AI control rule'
                  });
                }
              })
              .catch(error => {
                console.error('Error updating control rule:', error);
                setMessage({
                  type: 'error',
                  text: 'An error occurred while updating the AI control rule'
                });
              });

            setShowEditControlModal(false);
          }}
        />
      )}
    </PageContainer>
  );
}

// Add AI Control Modal Component
function AddAIControlModal({ isOpen, onClose, onSuccess }) {
  const [rules, setRules] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!rules) {
      setError('Rules content is required');
      setLoading(false);
      return;
    }

    try {
      const ruleData = {
        category: 'AI Control Rules', // Default category
        rules: rules,
        terms: rules // Keep terms for backward compatibility
      };

      const response = await aiControlsService.createRule(ruleData);

      if (response.success && response.data) {
        // Transform the response data to match the UI format
        const uiRule = {
          id: response.data.id,
          category: response.data.category,
          terms: response.data.rules || response.data.terms,
          rules: response.data.rules || response.data.terms,
          created_at: response.data.created_at,
          created_by: response.data.created_by,
          enabled: true
        };

        onSuccess(uiRule);
        // Reset form
        setRules('');
        onClose();
      } else {
        setError(response.error || 'Failed to add AI control rule');
      }
    } catch (error) {
      console.error('Error adding AI control:', error);
      setError(error.response?.data?.error || 'Failed to add AI control. Please try again.');
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle><FaPlus /> Add AI Control Rule</ModalTitle>
          <CloseButton onClick={onClose}><FaTimes /></CloseButton>
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            {error && (
              <Alert type="error">
                <AlertIcon><FaExclamationCircle /></AlertIcon>
                {error}
              </Alert>
            )}

            <FormGroup>
              <FormLabel htmlFor="rules">Rules Content</FormLabel>
              <FormTextArea
                id="rules"
                value={rules}
                onChange={e => setRules(e.target.value)}
                placeholder="Enter rules for AI control. Each line should be a specific rule or term to restrict."
                rows={8}
                required
                style={{ textAlign: 'left' }}
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Enter each rule on a new line. These rules will be used directly in the AI validator.
              </div>
            </FormGroup>

            <ModalFooter>
              <ActionButton type="button" onClick={onClose}>Cancel</ActionButton>
              <ActionButton type="submit" primary disabled={loading}>
                {loading ? <><FaSync className="fa-spin" /> Adding...</> : 'Add Control'}
              </ActionButton>
            </ModalFooter>
          </Form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
}

// Edit Control Modal Component
function EditControlModal({ isOpen, onClose, control, onSuccess }) {
  const [rules, setRules] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize form with rule data when the modal opens
  useEffect(() => {
    if (control) {
      // If terms exist, use them as initial rules
      if (control.terms) {
        setRules(control.terms);
      } else {
        // If rules already exist in the correct format, use them
        setRules(control.rules || '');
      }
    }
  }, [control]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!rules) {
      setError('Rules content is required');
      setLoading(false);
      return;
    }

    try {
      // Create the updated rule object
      const updatedRule = {
        ...control,
        category: 'AI Control Rules', // Default category
        terms: rules, // Keep terms for backward compatibility
        rules: rules  // Add rules field for the new format
      };

      onSuccess(updatedRule);
      setLoading(false);
    } catch (error) {
      console.error('Error updating AI control:', error);
      setError('Failed to update AI control. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle><FaEdit /> Edit AI Control Rule</ModalTitle>
          <CloseButton onClick={onClose}><FaTimes /></CloseButton>
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            {error && (
              <Alert type="error">
                <AlertIcon><FaExclamationCircle /></AlertIcon>
                {error}
              </Alert>
            )}

            <FormGroup>
              <FormLabel htmlFor="rules">Rules Content</FormLabel>
              <FormTextArea
                id="rules"
                value={rules}
                onChange={e => setRules(e.target.value)}
                placeholder="Enter rules for AI control. Each line should be a specific rule or term to restrict."
                rows={8}
                required
                style={{ textAlign: 'left' }}
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Enter each rule on a new line. These rules will be used directly in the AI validator.
              </div>
            </FormGroup>

            <ModalFooter>
              <ActionButton type="button" onClick={onClose}>Cancel</ActionButton>
              <ActionButton type="submit" primary disabled={loading}>
                {loading ? <><FaSync className="fa-spin" /> Updating...</> : 'Update Control'}
              </ActionButton>
            </ModalFooter>
          </Form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
}

export default DocumentsView;
